import React, { useState } from 'react';
import { X, Share2, Copy, Check, Link2 } from 'lucide-react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { fileShareService, ShareResult } from '../../../../core/FileShareService';
import { eventBus } from '../../../../core/EventBus';
import { t } from '../../i18n';

type ExpiryMode = 'duration' | 'date';
type DurationUnit = 'hour' | 'day';

// 复制到剪贴板：优先 Clipboard API（仅安全上下文可用），失败回退 execCommand。
// http 内网访问（非安全上下文）下 navigator.clipboard 不可用，必须有 execCommand 兜底。
async function copyTextWithFallback(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* 回退 execCommand */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

const ShareModal: React.FC = () => {
  const { shareTargets, closeShareDialog, peerId } = useFileManagerStore();

  const [password, setPassword] = useState('');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [expiryMode, setExpiryMode] = useState<ExpiryMode>('duration');
  const [durationValue, setDurationValue] = useState('1');
  const [durationUnit, setDurationUnit] = useState<DurationUnit>('day');
  const [expiredDate, setExpiredDate] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ShareResult | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  if (!shareTargets || shareTargets.length === 0) return null;

  const reset = () => {
    setPassword('');
    setMaxDownloads('');
    setExpiryMode('duration');
    setDurationValue('1');
    setDurationUnit('day');
    setExpiredDate('');
    setLoading(false);
    setResult(null);
    setCopiedKey(null);
  };

  const handleClose = () => {
    reset();
    closeShareDialog();
  };

  const copy = async (key: string, text: string) => {
    const ok = await copyTextWithFallback(text);
    if (ok) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } else {
      eventBus.emit('ui:toast', { message: '复制失败', type: 'error' });
    }
  };

  const handleSubmit = async () => {
    // 组装有效期：二选一
    const opts: { password?: string; maxDownloads?: number; expiresIn?: number; expiredAt?: string } = {};
    if (password) opts.password = password;
    const md = parseInt(maxDownloads, 10);
    if (Number.isFinite(md) && md > 0) opts.maxDownloads = md;

    if (expiryMode === 'duration') {
      const v = parseFloat(durationValue);
      if (!Number.isFinite(v) || v <= 0) {
        eventBus.emit('ui:toast', { message: t('shareInvalidDuration'), type: 'error' });
        return;
      }
      const seconds = durationUnit === 'hour' ? Math.round(v * 3600) : Math.round(v * 86400);
      opts.expiresIn = seconds;
    } else {
      if (!expiredDate) {
        eventBus.emit('ui:toast', { message: t('shareInvalidDate'), type: 'error' });
        return;
      }
      opts.expiredAt = expiredDate;
    }

    setLoading(true);
    try {
      const r = await fileShareService.createShare(shareTargets, peerId, opts);
      setResult(r);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      eventBus.emit('ui:toast', { message: t('shareFailed', { message }), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 text-sm rounded bg-[#1e1e1e] border border-white/10 text-slate-200 ' +
    'placeholder-slate-500 focus:outline-none focus:border-win-accent';

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-[#202020] w-[440px] max-h-[90vh] border border-white/10 rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#2d2d2d]">
          <span className="font-medium text-slate-200 flex items-center gap-2">
            <Share2 size={16} className="text-win-accent" />
            {result ? t('shareResultTitle') : t('shareTitle')}
          </span>
          <button onClick={handleClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 bg-[#1e1e1e] space-y-4">
          <div className="text-xs text-slate-400">{t('shareFileCount', { count: shareTargets.length })}</div>

          {!result ? (
            <>
              {/* 密码 */}
              <div className="space-y-1">
                <label className="text-xs text-slate-300">{t('sharePassword')}</label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('sharePasswordPlaceholder')}
                  className={inputCls}
                />
              </div>

              {/* 最大下载次数 */}
              <div className="space-y-1">
                <label className="text-xs text-slate-300">{t('shareMaxDownloads')}</label>
                <input
                  type="number"
                  min={1}
                  value={maxDownloads}
                  onChange={(e) => setMaxDownloads(e.target.value)}
                  placeholder={t('shareMaxDownloadsPlaceholder')}
                  className={inputCls}
                />
              </div>

              {/* 有效期：二选一 */}
              <div className="space-y-2">
                <label className="text-xs text-slate-300">{t('shareExpiry')}</label>
                <div className="flex gap-4 text-sm text-slate-200">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={expiryMode === 'duration'}
                      onChange={() => setExpiryMode('duration')}
                      className="accent-[var(--win-accent,#0078d4)]"
                    />
                    {t('shareExpiryDuration')}
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={expiryMode === 'date'}
                      onChange={() => setExpiryMode('date')}
                      className="accent-[var(--win-accent,#0078d4)]"
                    />
                    {t('shareExpiryDate')}
                  </label>
                </div>

                {expiryMode === 'duration' ? (
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min={0}
                      step="any"
                      value={durationValue}
                      onChange={(e) => setDurationValue(e.target.value)}
                      className={inputCls + ' flex-1'}
                    />
                    <select
                      value={durationUnit}
                      onChange={(e) => setDurationUnit(e.target.value as DurationUnit)}
                      className="px-3 py-2 text-sm rounded bg-[#1e1e1e] border border-white/10 text-slate-200 focus:outline-none focus:border-win-accent"
                    >
                      <option value="hour">{t('shareUnitHour')}</option>
                      <option value="day">{t('shareUnitDay')}</option>
                    </select>
                  </div>
                ) : (
                  <input
                    type="date"
                    value={expiredDate}
                    onChange={(e) => setExpiredDate(e.target.value)}
                    className={inputCls}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              {result.downloadUrlWithPassword && (
                <LinkRow
                  label={t('shareLinkWithPassword')}
                  url={result.downloadUrlWithPassword}
                  copied={copiedKey === 'pwd'}
                  onCopy={() => copy('pwd', result.downloadUrlWithPassword!)}
                />
              )}
              <LinkRow
                label={t('shareLinkNoPassword')}
                url={result.downloadUrl}
                copied={copiedKey === 'nopwd'}
                onCopy={() => copy('nopwd', result.downloadUrl)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 bg-[#2d2d2d] flex justify-end space-x-2">
          {!result ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-xs rounded bg-white/5 hover:bg-white/10 text-slate-300 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 text-xs rounded bg-win-accent hover:bg-blue-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('shareSubmitting') : t('shareSubmit')}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-xs rounded bg-win-accent hover:bg-blue-600 text-white transition-colors"
            >
              {t('shareDone')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const LinkRow: React.FC<{ label: string; url: string; copied: boolean; onCopy: () => void }> = ({
  label,
  url,
  copied,
  onCopy,
}) => (
  <div className="space-y-1">
    <div className="text-xs text-slate-400 flex items-center gap-1.5">
      <Link2 size={12} />
      {label}
    </div>
    <div className="flex gap-2">
      <input
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        className="flex-1 px-3 py-2 text-xs rounded bg-[#1e1e1e] border border-white/10 text-slate-300 focus:outline-none focus:border-win-accent"
      />
      <button
        onClick={onCopy}
        className="px-3 py-2 text-xs rounded bg-white/5 hover:bg-white/10 text-slate-200 transition-colors flex items-center gap-1 shrink-0"
      >
        {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        {copied ? t('shareCopied') : t('shareCopy')}
      </button>
    </div>
  </div>
);

export default ShareModal;
